import { MarketingShell } from '../../components/MarketingShell'
import { Seo } from '../../components/Seo'
import { Faq, faqJsonLd } from '../../components/Faq'
import { WaitlistCta } from '../../components/marketing/WaitlistForm'
import { Card, CardBody } from '../../components/ui/Card'

const features = [
  {
    title: 'Xero & QuickBooks integrations (Sage coming soon)',
    body: 'New invoices flow in automatically. Before each reminder goes out we check your ledger, and a paid or voided invoice is marked paid instead of chased.',
  },
  {
    title: 'CSV / Excel import',
    body: 'No accounting platform connected yet? Upload a spreadsheet and RemindLoop maps it straight in.',
  },
  {
    title: 'WhatsApp first, SMS as backup',
    body: 'Follow-ups go out automatically on your schedule, on the channel your customers actually read, with an automatic SMS fallback if WhatsApp cannot reach them.',
  },
  {
    title: 'Response-timed sending',
    body: 'Weighted toward the parts of the day people actually tend to respond, not one fixed hour for everyone.',
  },
  {
    title: 'A reminder schedule you control',
    body: 'Trigger by days overdue (or before due), pick the channel and message — start from a template or build your own.',
  },
  {
    title: 'Opt-out compliance built in',
    body: 'Anyone who replies STOP is opted out immediately and never messaged again, enforced centrally.',
  },
]

const faqItems = [
  {
    question: 'Which accounting platforms does RemindLoop integrate with?',
    answer:
      'Xero and QuickBooks today, with Sage coming soon. Each connection pulls in new invoices automatically, and RemindLoop checks the invoice with your accounting system just before every reminder, so a paid or voided invoice is never chased.',
  },
  {
    question: 'Can I use RemindLoop without connecting an accounting platform?',
    answer:
      'Yes — CSV and Excel import work independently of any accounting connection, so you can start sending reminders the same day even if you are not ready to connect Xero or QuickBooks.',
  },
  {
    question: 'What happens if a WhatsApp reminder fails to send?',
    answer:
      'RemindLoop automatically retries once over SMS — the most common reason a WhatsApp send fails is that the number is not on WhatsApp at all, and SMS reaches essentially any mobile number.',
  },
]

// See Home.tsx's matching comment for why these are exported.
export const seoTitle = 'Features — WhatsApp & SMS Follow-Ups, Xero/QuickBooks Sync & More'
export const seoDescription =
  'Automated WhatsApp & SMS invoice reminders, Xero/QuickBooks integrations (Sage coming soon) that check your ledger before every send, a reminder schedule you control, and full audit trails — see everything RemindLoop does.'
export const seoJsonLd = faqJsonLd(faqItems)

export default function Features() {
  return (
    <MarketingShell>
      <Seo title={seoTitle} description={seoDescription} path="/features" jsonLd={seoJsonLd} />
      <section className="mx-auto max-w-6xl px-[clamp(18px,4vw,44px)] py-[clamp(44px,7vw,88px)]">
        <div className="text-center">
          <h1 className="mx-auto max-w-[22ch] text-[clamp(30px,4.4vw,48px)] leading-[1.1] font-semibold tracking-[-0.03em]">
            Everything you need to get invoices followed up
          </h1>
          <p className="mx-auto mt-4.5 max-w-[58ch] text-[clamp(16px,1.5vw,18px)] leading-[1.6] text-foreground/76">
            RemindLoop follows up on WhatsApp and SMS so you don&rsquo;t have to, end to end, from
            import to send to compliance — for teams on Xero or QuickBooks (Sage coming soon), or
            working straight from a spreadsheet.
          </p>
          {/* Hero CTA added 2026-10-08 (user request: "a user should not
              have to scroll far to get to a call to action link") -- this
              page previously had none until after the full feature grid. */}
          <div className="mt-6">
            <WaitlistCta
              to="/register"
              source="features-hero"
              className="min-h-11 bg-magenta-700 px-6 py-3 text-base text-white hover:bg-magenta-800"
            >
              Join the waitlist
            </WaitlistCta>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {features.map((feature) => (
            <Card key={feature.title}>
              <CardBody className="space-y-2 p-6">
                <h2 className="text-[20px] leading-[1.25] font-semibold">{feature.title}</h2>
                <p className="text-[15px] leading-[1.6] text-foreground/76">{feature.body}</p>
              </CardBody>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <WaitlistCta
            to="/register"
            source="features-grid"
            className="min-h-11 bg-magenta-700 px-6 py-3 text-base text-white hover:bg-magenta-800"
          >
            Join the waitlist
          </WaitlistCta>
        </div>

        <div className="mx-auto mt-16 max-w-3xl border-t border-border pt-12">
          <Faq items={faqItems} />
        </div>
      </section>
    </MarketingShell>
  )
}
